import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { ArrowLeft, Save, X, Bell } from 'lucide-react';
import toast from 'react-hot-toast';
import { notificationsAPI } from '../../services/api';

const NotificationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      title: '',
      message: '',
      target_audience: 'employees',
      type: 'system',
    }
  });

  const targetAudience = watch('target_audience');

  // Notification types based on target audience
  const notificationTypes = {
    employees: [
      { value: 'system', label: 'System Alert' },
      { value: 'order', label: 'New Order Alert' },
      { value: 'stock', label: 'Stock Alert' },
      { value: 'policy', label: 'Policy Update' },
      { value: 'training', label: 'Training Announcement' },
      { value: 'maintenance', label: 'System Maintenance' },
      { value: 'update', label: 'General Update' }
    ],
    customers: [
      { value: 'promotion', label: 'Promotional Offer' },
      { value: 'offer', label: 'Special Offer' },
      { value: 'update', label: 'General Update' },
      { value: 'system', label: 'Service Notice' }
    ]
  };

  // Predefined templates for quick selection
  const notificationTemplates = {
    employees: {
      system: {
        title: 'System Alert',
        message: 'Important system alert: Please check your dashboard for critical updates and take necessary action.'
      },
      order: {
        title: 'New Order Alert',
        message: 'New order received! Please check the orders dashboard to process the pending order and update status.'
      },
      stock: {
        title: 'Stock Alert',
        message: 'Low stock alert: Several products are running low on inventory. Please review and restock as needed.'
      },
      policy: {
        title: 'Policy Update',
        message: 'Company policies have been updated. Please review the new policies in the employee handbook and acknowledge receipt.'
      },
      training: {
        title: 'Training Announcement',
        message: 'Mandatory training session scheduled for [DATE] at [TIME]. Please mark your calendars and ensure attendance.'
      },
      maintenance: {
        title: 'System Maintenance',
        message: 'Scheduled system maintenance on [DATE] from [TIME] to [TIME]. Please save your work and log out during this period.'
      },
      update: {
        title: 'Important Update',
        message: 'Important update for all employees: [DETAILS]. Please review and follow the updated procedures.'
      }
    },
    customers: {
      promotion: {
        title: '🎉 Special Promotion Just for You!',
        message: 'Exclusive offer: Get [DISCOUNT]% off on your next purchase! Use code [CODE] at checkout. Valid until [DATE].'
      },
      offer: {
        title: '🔥 Limited Time Offer!',
        message: 'Don\'t miss out! Special discount of [AMOUNT] on selected items. Shop now and save big! Offer expires [DATE].'
      },
      update: {
        title: '📢 Important Update',
        message: 'Important update for all customers: [DETAILS]. Please review and take necessary action if required.'
      },
      system: {
        title: '🔧 Service Notice',
        message: 'We will be performing scheduled maintenance on our system. During this time, some services may be temporarily unavailable. We apologize for any inconvenience.'
      }
    }
  };

  // Fetch notification data for editing
  const { data: notificationData, isLoading: isLoadingNotification } = useQuery(
    ['notification', id],
    () => notificationsAPI.getById ? notificationsAPI.getById(id) : Promise.reject('API not available'),
    {
      enabled: isEditing,
      onSuccess: (response) => {
        console.log('Notification form - API response:', response);
        
        // Handle different response structures
        let notification = null;
        if (response.data?.data) {
          notification = response.data.data;
        } else if (response.data) {
          notification = response.data;
        }

        console.log('Notification form - extracted data:', notification);
        
        if (notification) {
          const formData = {
            title: notification.title || '',
            message: notification.message || '',
            target_audience: notification.target_audience || 'employees',
            type: notification.type || 'system',
          };

          console.log('Notification form - form data to set:', formData);
          reset(formData);
        }
      },
      onError: (error) => {
        console.error('Failed to load notification:', error);
        if (isEditing) {
          toast.error('Failed to load notification data');
          navigate('/notifications');
        }
      }
    }
  );

  // Helper function to apply template
  const applyTemplate = (audience, type) => {
    const template = notificationTemplates[audience]?.[type];
    if (template) {
      const currentData = watch();
      reset({
        ...currentData,
        title: template.title,
        message: template.message,
        type: type // Keep the selected type
      });
    }
  };

  const mutation = useMutation(
    async (data) => {
      if (isEditing) {
        return notificationsAPI.update(id, data);
      } else {
        return notificationsAPI.create(data);
      }
    },
    {
      onSuccess: () => {
        toast.success(isEditing ? 'Notification updated successfully!' : 'Notification created successfully!');
        queryClient.invalidateQueries('notifications');
        navigate('/notifications');
      },
      onError: (error) => {
        console.error('Mutation error:', error);
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  );

  const onSubmit = async (data) => {
    mutation.mutate(data);
  };

  const handleCancel = () => {
    navigate('/notifications');
  };

  if (isEditing && isLoadingNotification) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Bell className="h-6 w-6" />
                {isEditing ? 'Edit Notification' : 'Create New Notification'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {isEditing ? 'Update notification details' : 'Create a new notification'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Target Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Audience <span className="text-red-500">*</span>
              </label>
              <select
                {...register('target_audience', { required: 'Target audience is required' })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              >
                <option value="employees">All Employees</option>
                <option value="customers">All Customers</option>
              </select>
              {errors.target_audience && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.target_audience.message}</p>
              )}
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notification Type <span className="text-red-500">*</span>
              </label>
              <select
                {...register('type', { required: 'Type is required' })}
                onChange={(e) => {
                  const selectedType = e.target.value;
                  if (selectedType) {
                    applyTemplate(targetAudience, selectedType);
                  }
                }}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Select notification type</option>
                {notificationTypes[targetAudience]?.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.type && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.type.message}</p>
              )}
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Select a type to auto-fill with a professional template
              </p>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('title', { required: 'Title is required' })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter notification title"
              />
              {errors.title && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.title.message}</p>
              )}
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('message', { required: 'Message is required' })}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter notification message"
              />
              {errors.message && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.message.message}</p>
              )}
            </div>

            {/* Template Info */}
            {targetAudience && (
              <div className={`p-4 rounded-lg ${targetAudience === 'employees' ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
                <h4 className={`text-sm font-medium mb-2 ${targetAudience === 'employees' ? 'text-blue-900 dark:text-blue-100' : 'text-green-900 dark:text-green-100'}`}>
                  {targetAudience === 'employees' ? '👥 Employee Notification' : '👤 Customer Notification'}
                </h4>
                <p className={`text-sm ${targetAudience === 'employees' ? 'text-blue-800 dark:text-blue-200' : 'text-green-800 dark:text-green-200'}`}>
                  {targetAudience === 'employees' 
                    ? 'This notification will be sent to all active employees for internal updates, alerts, and announcements.'
                    : 'This notification will be sent to all active customers for promotions, offers, and important updates.'
                  }
                </p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isLoading}
                className="flex-1 sm:flex-none px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {mutation.isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isEditing ? 'Update Notification' : 'Create Notification'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NotificationForm;